import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import './DepartmentManagersPanel.css';
// Reuse the slide-in drawer shell styles defined for the Users + Departments
// tabs so the three admin "open detail" surfaces feel identical.
import './usersTab/usersTab.css';
import { toastSuccess, toastError, errorMessage } from '../../lib/toast';

/**
 * Admin panel — manage the (possibly multiple) managers of each department.
 *
 * UX:
 *   - Row shows up to 2 manager avatars + a "+N more" overflow chip.
 *   - One "Manage managers" button per row opens a focused modal that holds
 *     the current managers (with remove × on each) plus a typeahead to add
 *     new managers. All add/remove happens inside the modal, so the table
 *     row stays compact and scannable.
 */
export default function DepartmentManagersPanel() {
  const [departments, setDepartments] = useState([]);
  const [summary, setSummary] = useState({ total: 0, managed: 0, unmanaged: 0, distinct_managers: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all | managed | unmanaged
  const [openDeptId, setOpenDeptId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API_BASE_URL}/api/admin/departments`, {
        withCredentials: true,
      });
      setDepartments(r.data.departments || []);
      setSummary(r.data.summary || { total: 0, managed: 0, unmanaged: 0, distinct_managers: 0 });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load departments.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onChanged = () => {
    load();
  };

  const filtered = departments.filter((d) => {
    const count = (d.managers || []).length;
    if (filter === 'managed') return count > 0;
    if (filter === 'unmanaged') return count === 0;
    return true;
  });

  const openDept = departments.find((d) => d.id === openDeptId) || null;

  return (
    // The admin topbar already titles this page; a second visible heading
    // repeated it. The section keeps its accessible name.
    <section className="dept-managers-panel" aria-label="Department managers">
      <header className="panel-header">
        <p className="hint">
          Managers see <strong>all tools</strong> used by their team and can request bulk access for missing members. Employees still see only their own tools.
        </p>
      </header>

      <div className="dm-overview" role="group" aria-label="Department coverage">
        <OverviewCard
          tone="neutral"
          label="Departments"
          value={summary.total}
          hint="In the system"
          onClick={() => setFilter('all')}
          isActive={filter === 'all'}
        />
        <OverviewCard
          tone="success"
          label="With manager"
          value={summary.managed}
          hint={`${summary.distinct_managers} distinct ${summary.distinct_managers === 1 ? 'person' : 'people'}`}
          onClick={() => setFilter('managed')}
          isActive={filter === 'managed'}
        />
        <OverviewCard
          tone={summary.unmanaged > 0 ? 'warning' : 'neutral'}
          label="Unmanaged"
          value={summary.unmanaged}
          hint={summary.unmanaged > 0 ? 'No one to triage requests' : 'All covered'}
          onClick={() => setFilter('unmanaged')}
          isActive={filter === 'unmanaged'}
        />
      </div>

      {loading && <p className="empty-state">Loading departments…</p>}

      {!loading && error && (
        <div className="error-state" role="alert">
          <p>{error}</p>
          <button type="button" onClick={load} className="btn-secondary">Retry</button>
        </div>
      )}

      {!loading && !error && filtered.length === 0 && departments.length === 0 && (
        <div className="empty-state">
          <p>No departments yet.</p>
          <small>Create a department first, then come back to assign a manager.</small>
        </div>
      )}

      {!loading && !error && filtered.length === 0 && departments.length > 0 && (
        <div className="empty-state">
          <p>No departments match this filter.</p>
          <button type="button" className="btn-secondary" onClick={() => setFilter('all')}>
            Show all
          </button>
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className="admin-table-wrap ut-tablewrap">
          <table className="admin-table ut-table dept-managers-table" aria-label="Departments">
            <thead>
              <tr>
                <th scope="col">Department</th>
                <th scope="col">Members</th>
                <th scope="col">Catalog</th>
                <th scope="col">Managers</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((dept) => (
                <DepartmentRow
                  key={dept.id}
                  dept={dept}
                  isSelected={dept.id === openDeptId}
                  onOpen={() => setOpenDeptId(dept.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openDept && (
        <ManagersModal
          dept={openDept}
          onClose={() => setOpenDeptId(null)}
          onChanged={onChanged}
        />
      )}
    </section>
  );
}

function OverviewCard({ tone, label, value, hint, onClick, isActive }) {
  return (
    <button
      type="button"
      className={`dm-stat dm-stat--${tone} ${isActive ? 'is-active' : ''}`}
      onClick={onClick}
      aria-pressed={isActive}
    >
      <span className="dm-stat-value">{value}</span>
      <span className="dm-stat-label">{label}</span>
      {hint && <span className="dm-stat-hint">{hint}</span>}
    </button>
  );
}

function DepartmentRow({ dept, isSelected, onOpen }) {
  const managers = dept.managers || [];
  return (
    <tr
      className={`ut-tr dm-row ${isSelected ? 'is-selected' : ''}`}
      tabIndex={0}
      role="button"
      aria-label={`Manage managers for ${dept.name}`}
      aria-pressed={isSelected}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <td className="at-primary">
        <div className="ut-cell-name">{dept.name}</div>
        {dept.description && <div className="ut-cell-email">{dept.description}</div>}
      </td>
      <td>
        <span className="member-count">{dept.member_count}</span>
      </td>
      <td>
        <CatalogLegend dept={dept} />
      </td>
      <td>
        <ManagerSummary managers={managers} />
      </td>
    </tr>
  );
}

function ManagerSummary({ managers }) {
  if (managers.length === 0) {
    return (
      <span className="manager-summary manager-summary--empty">
        — none assigned —
      </span>
    );
  }
  const visible = managers.slice(0, 2);
  const overflow = managers.length - visible.length;
  return (
    <span
      className="manager-summary"
      aria-label={`${managers.length} manager${managers.length === 1 ? '' : 's'}: ${managers.map((m) => m.name).join(', ')}`}
    >
      <span className="avatar-stack">
        {visible.map((m) => (
          <span key={m.id} className="avatar" aria-hidden="true">{initials(m.name)}</span>
        ))}
        {overflow > 0 && <span className="avatar avatar--more" aria-hidden="true">+{overflow}</span>}
      </span>
      <span className="manager-summary-text">
        {managers.length === 1
          ? <strong>{managers[0].name}</strong>
          : <><strong>{managers[0].name}</strong> <span className="muted">and {managers.length - 1} more</span></>}
      </span>
    </span>
  );
}

function CatalogLegend({ dept }) {
  const total = dept.catalog_count ?? 0;
  const defaults = dept.default_count ?? 0;
  const managersOnly = dept.managers_only_count ?? 0;
  const inUse = dept.in_use_count ?? 0;
  return (
    <div className="catalog-legend">
      <div className="catalog-legend-line">
        <strong>{total}</strong> tools in catalog
      </div>
      <div className="catalog-legend-detail">
        {defaults} dept default{defaults === 1 ? '' : 's'}
        {managersOnly > 0 && <> · {managersOnly} managers-only</>}
        {' '}· {inUse} in use
      </div>
      <div className="catalog-legend-rule">
        Managers see all <strong>{total}</strong> · employees see only their own.
      </div>
    </div>
  );
}

function ManagersModal({ dept, onClose, onChanged }) {
  const managers = dept.managers || [];
  const [busy, setBusy] = useState(false);
  const dialogRef = useRef(null);

  useEffect(() => {
    dialogRef.current?.querySelector('input[type="search"]')?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const addManager = async (user) => {
    setBusy(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/admin/departments/${dept.id}/managers`,
        { user_id: user.id },
        { withCredentials: true }
      );
      toastSuccess(`Added ${user.name} as manager of ${dept.name}.`);
      onChanged();
    } catch (err) {
      toastError(errorMessage(err, "Couldn't add the manager. Try again."));
    } finally {
      setBusy(false);
    }
  };

  const removeManager = async (user) => {
    if (!window.confirm(`Remove ${user.name} as a manager of ${dept.name}?`)) return;
    setBusy(true);
    try {
      await axios.delete(
        `${API_BASE_URL}/api/admin/departments/${dept.id}/managers/${user.id}`,
        { withCredentials: true }
      );
      toastSuccess(`Removed ${user.name} as manager of ${dept.name}.`);
      onChanged();
    } catch (err) {
      toastError(errorMessage(err, "Couldn't remove the manager. Try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div
        className="ut-drawer-backdrop is-open"
        aria-hidden="true"
        onClick={() => !busy && onClose()}
      />
      <aside
        ref={dialogRef}
        className="ut-drawer is-open"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`mm-title-${dept.id}`}
      >
        <header
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 12,
            padding: '16px 20px',
            borderBottom: '1px solid #e2e8f0',
            position: 'sticky',
            top: 0,
            background: 'white',
            zIndex: 1,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <p className="eyebrow" style={{ margin: 0, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#718096', fontWeight: 600 }}>Department</p>
            <h3 id={`mm-title-${dept.id}`} style={{ margin: '2px 0 4px', fontSize: 20, fontWeight: 700, color: '#0F0F10' }}>{dept.name}</h3>
            <p className="dm-modal-sub" style={{ margin: 0, fontSize: 13, color: '#718096' }}>
              {managers.length === 0
                ? 'No managers yet.'
                : `${managers.length} manager${managers.length === 1 ? '' : 's'} · ${dept.member_count} active member${dept.member_count === 1 ? '' : 's'}`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close department managers"
            style={{ width: 32, height: 32, borderRadius: 8, border: 'none', background: 'transparent', cursor: busy ? 'not-allowed' : 'pointer', fontSize: 22, lineHeight: 1, color: '#4a5568', flexShrink: 0 }}
          >
            ×
          </button>
        </header>

        <div className="ut-drawer-body">
          <section className="mm-section" aria-labelledby={`mm-current-${dept.id}`}>
            <h4 id={`mm-current-${dept.id}`}>Current managers</h4>
            {managers.length === 0 && (
              <p className="empty">No one manages this department yet. Add someone below.</p>
            )}
            {managers.length > 0 && (
              <ul className="mm-list">
                {managers.map((m) => (
                  <li key={m.id}>
                    <span className="avatar" aria-hidden="true">{initials(m.name)}</span>
                    <span className="mm-list-text">
                      <strong>{m.name}</strong>
                      <small>{m.email}</small>
                    </span>
                    <button
                      type="button"
                      className="btn-link btn-link--danger"
                      onClick={() => removeManager(m)}
                      disabled={busy}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="mm-section" aria-labelledby={`mm-add-${dept.id}`}>
            <h4 id={`mm-add-${dept.id}`}>Add manager</h4>
            <ManagerSearch
              dept={dept}
              existing={managers}
              busy={busy}
              onPick={addManager}
            />
          </section>

          <footer style={{ display: 'flex', justifyContent: 'flex-end', padding: '16px 20px', borderTop: '1px solid #e2e8f0' }}>
            <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
              Done
            </button>
          </footer>
        </div>
      </aside>
    </>
  );
}

function ManagerSearch({ dept, existing, busy, onPick }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const existingIds = useMemo(() => new Set(existing.map((m) => m.id)), [existing]);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    const handle = window.setTimeout(async () => {
      setSearching(true);
      try {
        const r = await axios.get(`${API_BASE_URL}/api/admin/users/manager-candidates`, {
          params: { q: query },
          withCredentials: true,
        });
        const users = (r.data.users || []).filter((u) => !existingIds.has(u.id));
        setResults(users);
        setActiveIndex(users.length > 0 ? 0 : -1);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => window.clearTimeout(handle);
  }, [query, existingIds]);

  const onKeyDown = (e) => {
    if (results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(results.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      onPick(results[activeIndex]);
      setQuery('');
      setResults([]);
    }
  };

  return (
    <div className="mm-search">
      <label className="visually-hidden" htmlFor={`mm-search-${dept.id}`}>
        Search active users by name or email
      </label>
      <input
        id={`mm-search-${dept.id}`}
        type="search"
        autoComplete="off"
        placeholder="Search by name or email…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={busy}
      />

      {searching && <p className="hint">Searching…</p>}

      {results.length > 0 && (
        <ul className="mm-results" role="listbox" aria-label="Matching users">
          {results.map((u, i) => (
            <li key={u.id} role="option" aria-selected={i === activeIndex}>
              <button
                type="button"
                className={i === activeIndex ? 'active' : ''}
                onClick={() => {
                  onPick(u);
                  setQuery('');
                  setResults([]);
                }}
                disabled={busy}
              >
                <span className="avatar" aria-hidden="true">{initials(u.name)}</span>
                <span className="mm-list-text">
                  <strong>{u.name}</strong>
                  <small>{u.email}</small>
                </span>
                <span className="add-hint">Add →</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {query.trim().length >= 2 && !searching && results.length === 0 && (
        <p className="hint">No matches. Try a different search.</p>
      )}
    </div>
  );
}

function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || '').join('') || '?';
}
